import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The ops design kit — every staff screen builds from these so the suite
 * reads as one product. Tokens come from tailwind.config.js (the portal's
 * existing brand system): blue-primary #223BF4, blue-100 #EEF0FE, the
 * gray ink scale, and the green/yellow/red status tints.
 */

// ── Buttons ────────────────────────────────────────────────────────────────
export const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-blue-primary px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-one disabled:cursor-not-allowed disabled:opacity-50';
export const btnSecondary =
  'inline-flex items-center gap-1.5 rounded-md border border-white-two bg-white px-3.5 py-2 text-sm font-medium text-gray-five transition-colors hover:border-gray-two hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-50';
export const btnGhost =
  'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-primary transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50';
export const btnDanger =
  'inline-flex items-center gap-1.5 rounded-md border border-red-500 bg-white px-3.5 py-2 text-sm font-medium text-red-one transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50';

// ── Form fields ────────────────────────────────────────────────────────────
export const inputClass =
  'rounded-md border border-white-two bg-white px-3 py-2 text-sm text-gray-four placeholder:text-gray-two focus:border-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-100';
export const labelClass =
  'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-bold';

// ── Surfaces ───────────────────────────────────────────────────────────────
export const cardClass =
  'overflow-hidden rounded-xl border border-white-regular bg-white';

export const Card: React.FC<{
  title?: string;
  right?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, right, className, children }) => (
  <section className={`${cardClass} ${className ?? ''}`}>
    {(title || right) && (
      <header className="flex items-center justify-between gap-3 border-b border-white-three px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-four">{title}</h2>
        {right}
      </header>
    )}
    {children}
  </section>
);

// ── Page scaffolding ───────────────────────────────────────────────────────
export const PageHeader: React.FC<{
  title: string;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  back?: { to: string; label: string };
}> = ({ title, meta, actions, back }) => (
  <div className="px-8 pb-4 pt-6">
    {back && (
      <Link
        to={back.to}
        className="mb-1 inline-block text-xs font-medium text-gray-bold transition-colors hover:text-blue-primary"
      >
        ← {back.label}
      </Link>
    )}
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-four">
          {title}
        </h1>
        {meta && <div className="mt-1 text-sm text-gray-three">{meta}</div>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  </div>
);

export const Toolbar: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex flex-wrap items-center gap-2 px-8 pb-4">{children}</div>
);

export const PageBody: React.FC<{
  children: React.ReactNode;
  wide?: boolean;
}> = ({ children, wide }) => (
  <div className={`px-8 pb-24 ${wide ? '' : 'max-w-6xl'}`}>{children}</div>
);

// ── Tables ─────────────────────────────────────────────────────────────────
export const tableClass =
  'w-full text-left text-sm text-gray-five [font-variant-numeric:tabular-nums]';
export const theadClass =
  'bg-grey-50 text-[11px] uppercase tracking-wider text-gray-bold';
export const thClass = 'px-4 py-2.5 font-semibold';
export const tdClass = 'border-t border-white-three px-4 py-2.5';
export const rowClickClass =
  'cursor-pointer transition-colors hover:bg-blue-100/40';

// ── Sortable headers ───────────────────────────────────────────────────────
// One convention for every ops list: the route takes `sort=<key>&dir=`,
// the header toggles asc → desc → off. (Staff feedback 2026-09-25.)
export type SortDir = 'asc' | 'desc';
export function useListSort(defaultKey = '', defaultDir: SortDir = 'asc') {
  const [sortKey, setSortKey] = React.useState(defaultKey);
  const [sortDir, setSortDir] = React.useState<SortDir>(defaultDir);
  const toggleSort = (k: string) => {
    if (sortKey !== k) {
      setSortKey(k);
      setSortDir('asc');
    } else if (sortDir === 'asc') setSortDir('desc');
    else {
      setSortKey('');
      setSortDir('asc');
    }
  };
  return { sortKey, sortDir, toggleSort };
}

export const SortTh: React.FC<{
  k: string;
  label: React.ReactNode;
  sort: string;
  dir: SortDir;
  onSort: (k: string) => void;
  right?: boolean;
  className?: string;
}> = ({ k, label, sort, dir, onSort, right, className }) => {
  const active = sort === k;
  return (
    <th
      className={`${thClass} ${right ? 'text-right' : ''} ${
        className ?? ''
      } select-none`}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-four ${
          active ? 'text-gray-four' : ''
        }`}
        onClick={() => onSort(k)}
        title="Sort by this column"
      >
        {label}
        <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
};

// ── Status pills ───────────────────────────────────────────────────────────
type Tone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  good: 'bg-green-500 text-green-900',
  warn: 'bg-yellow-500 text-yellow-900',
  bad: 'bg-red-500 text-red-900',
  info: 'bg-blue-100 text-blue-primary',
  neutral: 'bg-grey-100 text-grey-700',
};

const GOOD = ['available', 'active', 'completed', 'closed won', 'open'];
const WARN = ['rescheduled', 'hold', 'on hold', 'pending', 'in progress'];
const BAD = ['cancelled', 'canceled', 'closed lost', 'no attend', 'expired'];

export function statusTone(status?: string | null): Tone {
  const s = (status ?? '').toLowerCase();
  if (!s) return 'neutral';
  if (BAD.some((k) => s.includes(k))) return 'bad';
  if (WARN.some((k) => s.includes(k))) return 'warn';
  if (GOOD.some((k) => s.includes(k))) return 'good';
  return 'info';
}

export const Pill: React.FC<{ value?: string | null; tone?: Tone }> = ({
  value,
  tone,
}) =>
  value ? (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
        TONE_CLASS[tone ?? statusTone(value)]
      }`}
    >
      {value}
    </span>
  ) : (
    <span className="text-gray-two">—</span>
  );

/** Marks rows born in the portal (LOCAL-*) vs mirrored from Salesforce. */
export const OriginBadge: React.FC<{ local?: boolean }> = ({ local }) =>
  local ? <Pill value="Portal" tone="info" /> : null;

// ── States ─────────────────────────────────────────────────────────────────
export const EmptyState: React.FC<{
  title: string;
  hint?: string;
  action?: React.ReactNode;
}> = ({ title, hint, action }) => (
  <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
    <div className="text-sm font-semibold text-gray-five">{title}</div>
    {hint && <div className="max-w-sm text-sm text-gray-bold">{hint}</div>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

export const Pagination: React.FC<{
  page: number;
  pages: number;
  total?: number;
  onPage: (p: number) => void;
}> = ({ page, pages, total, onPage }) =>
  pages <= 1 ? null : (
    <div className="flex items-center justify-between gap-3 border-t border-white-three px-5 py-3 text-sm text-gray-three">
      <span>
        Page {page} of {pages}
        {total != null && ` · ${total.toLocaleString()} rows`}
      </span>
      <span className="flex gap-1.5">
        <button
          className={btnSecondary}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <button
          className={btnSecondary}
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </span>
    </div>
  );
